import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Trash2, Plus, Pencil, X, Save, Loader2 } from "lucide-react";
import { CATEGORIES, getAllDesigns } from "../../data/catalog";
import FileUpload, { absolutize } from "../../components/admin/FileUpload";
import { defaultGenderFor, genderOf } from "../../lib/gender";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -------------------------------------------------------------
// Designs
// -------------------------------------------------------------
const EMPTY_DESIGN = {
  name: "",
  category: "gold",
  subCategory: "rings",
  styleName: "",
  image: "",
  gallery: [],
  video: "",
  weight: 0,
  weightUnit: "g",
  purity: "22k",
  hallmark: "BIS Hallmark 916",
  makingCharge: "On request",
  description: "",
  isExclusive: false,
  gender: "unisex",
};

const GENDER_OPTIONS = [
  { slug: "men", label: "Men" },
  { slug: "ladies", label: "Ladies" },
  { slug: "unisex", label: "Unisex" },
  { slug: "kids", label: "Kids" },
];

function DesignsTab() {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subcats, setSubcats] = useState([]); // custom sub-cats from server
  const [form, setForm] = useState(EMPTY_DESIGN);
  const [editingId, setEditingId] = useState(null); // null = create mode
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [showBrowse, setShowBrowse] = useState(false);
  const [designQuery, setDesignQuery] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState("");
  const [designFilterCat, setDesignFilterCat] = useState("all");
  const [designFilterGender, setDesignFilterGender] = useState("all");
  const formRef = React.useRef(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/designs`),
      axios.get(`${API}/admin/subcategories`),
    ])
      .then(([d, s]) => {
        setDesigns(d.data);
        setSubcats(s.data);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Combine default sub-categories with custom ones for the picker
  const subOptions = useMemo(() => {
    const items = [];
    for (const cat of CATEGORIES) {
      for (const s of cat.subCategories) {
        items.push({ parent: cat.slug, slug: s.slug, label: `${cat.label} → ${s.label}` });
      }
    }
    for (const c of subcats) {
      const cat = CATEGORIES.find((x) => x.slug === c.parentCategory);
      items.push({
        parent: c.parentCategory,
        slug: c.slug,
        label: `${cat ? cat.label : c.parentCategory} → ${c.label} (custom)`,
      });
    }
    return items.filter((s) => s.parent === form.category);
  }, [form.category, subcats]);

  // Search + category filter for the admin design list
  const filteredDesigns = useMemo(() => {
    const q = designQuery.trim().toLowerCase();
    return designs.filter((d) => {
      if (designFilterCat !== "all" && d.category !== designFilterCat) return false;
      if (designFilterGender !== "all" && genderOf({ ...d, subCategory: d.subCategory }) !== designFilterGender) return false;
      if (!q) return true;
      const hay = [
        d.name, d.description, d.category, d.subCategory,
        d.purity, String(d.weight ?? ""), d.gender,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [designs, designQuery, designFilterCat, designFilterGender]);

  const update = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      // When the admin changes category or sub-category, auto-suggest a
      // sensible default gender IF they haven't manually picked one yet —
      // this avoids necklaces showing up under "Men" by mistake.
      if ((k === "subCategory" || k === "category") && next.subCategory) {
        const suggested = defaultGenderFor(next.subCategory);
        // Only overwrite when the current pick is the untouched default "unisex".
        if (f.gender === "unisex" || !f.gender) next.gender = suggested;
      }
      return next;
    });
  };

  const startEdit = (d) => {
    setEditingId(d.id);
    setError("");
    setOk("");
    setForm({
      name: d.name || "",
      category: d.category || "gold",
      subCategory: d.subCategory || "rings",
      styleName: d.styleName || "",
      image: d.image || "",
      gallery: d.gallery || [],
      video: d.video || "",
      weight: d.weight || 0,
      weightUnit: d.weightUnit || "g",
      purity: d.purity || "22k",
      hallmark: d.hallmark || "BIS Hallmark 916",
      makingCharge: d.makingCharge || "On request",
      description: d.description || "",
      isExclusive: !!d.isExclusive,
      gender: d.gender || "unisex",
    });
    // scroll form into view
    setTimeout(() => {
      if (formRef.current) formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 40);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_DESIGN);
    setError("");
    setOk("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setOk("");
    if (!form.name.trim() || !form.image) {
      setError("Please add a name and an image.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, weight: Number(form.weight) || 0 };
      if (editingId) {
        await axios.put(`${API}/admin/designs/${editingId}`, payload);
        setOk("Changes saved ✓");
        setEditingId(null);
      } else {
        await axios.post(`${API}/admin/designs`, payload);
        setOk("Design added ✓");
      }
      setForm(EMPTY_DESIGN);
      load();
      setTimeout(() => setOk(""), 2500);
    } catch (err) {
      setError(
        (err && err.response && err.response.data && err.response.data.detail) ||
          "Failed to save"
      );
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this design?")) return;
    try {
      await axios.delete(`${API}/admin/designs/${id}`);
      if (editingId === id) cancelEdit();
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      alert(
        `Could not delete this design.\n${detail || err.message || ""}${
          status ? ` (HTTP ${status})` : ""
        }`
      );
    }
  };

  const isEditing = editingId !== null;

  return (
    <div>
      <h1 className="admin-h1">Designs</h1>
      <p className="admin-sub">
        Add new jewellery pieces or edit existing ones — changes appear on the website instantly.
      </p>

      <div style={{ marginBottom: 18, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => setShowBrowse(true)}
          data-testid="admin-browse-defaults"
        >
          <Pencil size={14} /> Browse & Edit built-in designs
        </button>
        <BulkDescriptionsButton onDone={load} />
        <OptimizeVideosButton />
        <OptimizeImagesButton />
      </div>

      {showBrowse && (
        <BuiltInBrowser
          onClose={() => setShowBrowse(false)}
          onImport={(newDoc) => {
            setShowBrowse(false);
            load();
            // pre-fill form with the newly created custom design so admin can keep editing
            startEdit(newDoc);
          }}
        />
      )}

      <div className="admin-card" ref={formRef}>
        <h3>
          {isEditing ? (
            <>
              Editing design{" "}
              <span className="admin-count" style={{ fontFamily: "monospace" }}>
                {editingId}
              </span>
            </>
          ) : (
            "Add a new design"
          )}
        </h3>
        <form onSubmit={submit} className="admin-form">
          <div className="admin-row two">
            <label>
              <span>Name *</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="e.g. Italian Band Ring 007"
                required
                data-testid="admin-design-name"
              />
            </label>
            <label>
              <span>Style / short label</span>
              <input
                type="text"
                value={form.styleName}
                onChange={(e) => update("styleName", e.target.value)}
                placeholder="e.g. Italian Band"
                data-testid="admin-design-style"
              />
            </label>
          </div>

          <div className="admin-row two">
            <label>
              <span>Category *</span>
              <select
                value={form.category}
                onChange={(e) => {
                  update("category", e.target.value);
                  const first = CATEGORIES.find((c) => c.slug === e.target.value);
                  update("subCategory", first ? first.subCategories[0].slug : "");
                }}
                data-testid="admin-design-category"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Sub-category *</span>
              <select
                value={form.subCategory}
                onChange={(e) => update("subCategory", e.target.value)}
                data-testid="admin-design-subcategory"
              >
                {subOptions.map((s) => (
                  <option key={s.slug} value={s.slug}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="admin-row three">
            <label>
              <span>Weight (g) *</span>
              <input
                type="number"
                step="0.01"
                value={form.weight}
                onChange={(e) => update("weight", e.target.value)}
                required
                data-testid="admin-design-weight"
              />
            </label>
            <label>
              <span>Purity *</span>
              <input
                type="text"
                value={form.purity}
                onChange={(e) => update("purity", e.target.value)}
                data-testid="admin-design-purity"
              />
            </label>
            <label>
              <span>Making Charge</span>
              <input
                type="text"
                value={form.makingCharge}
                onChange={(e) => update("makingCharge", e.target.value)}
              />
            </label>
          </div>
          <div className="admin-row three">
            <label>
              <span>For (Gender)</span>
              <select
                value={form.gender || "unisex"}
                onChange={(e) => update("gender", e.target.value)}
                data-testid="admin-design-gender"
              >
                {GENDER_OPTIONS.map((g) => (
                  <option key={g.slug} value={g.slug}>{g.label}</option>
                ))}
              </select>
            </label>
          </div>

          <label>
            <span>Hallmark / Certification</span>
            <input
              type="text"
              value={form.hallmark}
              onChange={(e) => update("hallmark", e.target.value)}
            />
          </label>

          <label>
            <span
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}
            >
              <span>Description (optional)</span>
              <button
                type="button"
                onClick={async () => {
                  if (!form.name.trim()) {
                    setAiError("Give the design a name first, then I'll draft the description.");
                    return;
                  }
                  setAiError("");
                  setAiBusy(true);
                  try {
                    const { data } = await axios.post(`${API}/admin/ai/description`, {
                      name: form.name,
                      category: form.category,
                      subCategory: form.subCategory,
                      weight: Number(form.weight) || undefined,
                      weightUnit: form.weightUnit,
                      purity: form.purity,
                      gender: form.gender,
                      makingCharge: form.makingCharge,
                      hallmark: form.hallmark,
                      styleName: form.styleName,
                    });
                    update("description", data.description || "");
                  } catch (e) {
                    setAiError(e?.response?.data?.detail || "AI service unavailable — try again in a moment.");
                  } finally {
                    setAiBusy(false);
                  }
                }}
                disabled={aiBusy}
                data-testid="admin-design-ai-desc"
                className="btn btn-outline"
                style={{ padding: "5px 12px", fontSize: 12, opacity: aiBusy ? 0.7 : 1 }}
              >
                {aiBusy ? (
                  <>
                    <Loader2 size={13} className="spin" /> Writing…
                  </>
                ) : (
                  <>✨ Generate with AI</>
                )}
              </button>
            </span>
            <textarea
              rows={4}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Tell customers what makes this piece special… or tap 'Generate with AI' above."
              data-testid="admin-design-desc"
            />
            {aiError && (
              <em style={{ color: "#B45252", fontSize: 12, marginTop: 4 }} data-testid="admin-design-ai-error">
                {aiError}
              </em>
            )}
          </label>

          <label style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
            <input
              type="checkbox"
              checked={!!form.isExclusive}
              onChange={(e) => update("isExclusive", e.target.checked)}
              data-testid="admin-design-exclusive"
              style={{ width: 18, height: 18 }}
            />
            <span>
              <strong>Feature in Exclusive Collection</strong>
              <br />
              <em style={{ color: "#6B4A2F", fontSize: 12 }}>
                Adds this piece to the &quot;Exclusive Collection&quot; section on the homepage.
              </em>
            </span>
          </label>

          <AiStudioSection
            onApply={({ image, gallery, video }) => {
              if (image) update("image", image);
              if (gallery && gallery.length) update("gallery", gallery);
              if (video) update("video", video);
            }}
          />

          <div className="admin-row two">
            <FileUpload
              label="Main Photo *"
              value={form.image}
              onChange={(u) => update("image", u)}
              accept="image/*"
              kind="image"
            />
            <FileUpload
              label="Short Video (optional)"
              value={form.video || ""}
              onChange={(u) => update("video", u)}
              accept="video/mp4,video/webm,video/quicktime"
              kind="video"
            />
          </div>

          {/* Extra gallery images — up to 3 more shots (side / detail / model). */}
          <div className="admin-gallery-strip">
            <div className="admin-gallery-strip-head">
              <strong>Extra gallery photos</strong>
              <span className="admin-muted">
                Up to 3 more shots — side / close-up / on-model. Any format we auto-optimise.
              </span>
            </div>
            <div className="admin-row three">
              {[0, 1, 2].map((idx) => (
                <FileUpload
                  key={idx}
                  label={`Gallery photo ${idx + 1}`}
                  value={(form.gallery && form.gallery[idx]) || ""}
                  onChange={(u) => {
                    const arr = [...(form.gallery || [])];
                    if (u) {
                      arr[idx] = u;
                    } else {
                      // clearing a slot removes it
                      arr.splice(idx, 1);
                    }
                    update("gallery", arr.filter(Boolean));
                  }}
                  accept="image/*"
                  kind="image"
                />
              ))}
            </div>
          </div>

          {error && <div className="admin-error">{error}</div>}
          {ok && <div className="admin-ok">{ok}</div>}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              data-testid="admin-design-save"
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="fu-spin" />{" "}
                  {isEditing ? "Saving…" : "Saving…"}
                </>
              ) : isEditing ? (
                <>
                  <Save size={16} /> Save Changes
                </>
              ) : (
                <>
                  <Plus size={16} /> Add Design
                </>
              )}
            </button>
            {isEditing && (
              <button
                type="button"
                className="btn btn-outline"
                onClick={cancelEdit}
                data-testid="admin-design-cancel"
              >
                <X size={16} /> Cancel edit
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="admin-card">
        <h3>
          Your custom designs <span className="admin-count">({filteredDesigns.length}/{designs.length})</span>
        </h3>
        <div
          className="admin-filter-row"
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            alignItems: "center",
            margin: "6px 0 14px",
          }}
        >
          <input
            type="search"
            value={designQuery}
            onChange={(e) => setDesignQuery(e.target.value)}
            placeholder="Search by name, category, sub-category, purity…"
            data-testid="admin-designs-search"
            style={{
              flex: "1 1 260px",
              minWidth: 220,
              padding: "9px 14px",
              borderRadius: 10,
              border: "1px solid rgba(139,90,43,0.35)",
              fontSize: 14,
            }}
          />
          <select
            value={designFilterCat}
            onChange={(e) => setDesignFilterCat(e.target.value)}
            data-testid="admin-designs-filter-cat"
            style={{
              padding: "9px 12px",
              borderRadius: 10,
              border: "1px solid rgba(139,90,43,0.35)",
              fontSize: 14,
              background: "#fff",
              minWidth: 160,
            }}
          >
            <option value="all">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c.slug} value={c.slug}>{c.label}</option>
            ))}
          </select>
          <select
            value={designFilterGender}
            onChange={(e) => setDesignFilterGender(e.target.value)}
            data-testid="admin-designs-filter-gender"
            style={{
              padding: "9px 12px",
              borderRadius: 10,
              border: "1px solid rgba(139,90,43,0.35)",
              fontSize: 14,
              background: "#fff",
              minWidth: 140,
            }}
          >
            <option value="all">All genders</option>
            {GENDER_OPTIONS.map((g) => (
              <option key={g.slug} value={g.slug}>{g.label}</option>
            ))}
          </select>
        </div>
        {loading ? (
          <p className="admin-muted">Loading…</p>
        ) : filteredDesigns.length === 0 ? (
          <p className="admin-muted">
            {designs.length === 0
              ? "No custom designs yet. Add one above — it'll show up alongside the built-in collection immediately."
              : "No designs match this filter. Clear the search or change the category to see more."}
          </p>
        ) : (
          <div className="admin-list">
            {filteredDesigns.map((d) => (
              <div
                className={`admin-list-item ${editingId === d.id ? "editing" : ""}`}
                key={d.id}
              >
                <div className="admin-list-thumb">
                  {d.image ? <img src={absolutize(d.image)} alt={d.name} /> : null}
                </div>
                <div className="admin-list-body">
                  <strong>{d.name}</strong>
                  <span>
                    {d.category} · {d.subCategory} · {d.purity} · {d.weight}
                    {d.weightUnit || "g"}
                    {" · "}
                    <em
                      data-testid={`admin-list-gender-${d.id}`}
                      style={{
                        fontStyle: "normal",
                        padding: "1px 8px",
                        borderRadius: 999,
                        background: "rgba(139,90,43,0.10)",
                        color: "#6B4A2F",
                        fontSize: 11,
                        textTransform: "capitalize",
                      }}
                    >
                      {d.gender || "unisex"}
                    </em>
                  </span>
                </div>
                <button
                  type="button"
                  className="admin-edit"
                  onClick={() => startEdit(d)}
                  aria-label="Edit"
                  data-testid={`admin-edit-${d.id}`}
                  title="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  className="admin-del"
                  onClick={() => del(d.id)}
                  aria-label="Delete"
                  data-testid={`admin-delete-${d.id}`}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Browse & edit built-in designs
// -------------------------------------------------------------
function BuiltInBrowser({ onClose, onImport }) {
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [hiddenIds, setHiddenIds] = useState(() => new Set());
  const PAGE = 24;
  const [shown, setShown] = useState(PAGE);

  const all = useMemo(() => getAllDesigns(), []);
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return all.filter((d) => {
      if (category && d.category.slug !== category) return false;
      if (query) {
        const hay = `${d.name} ${d.styleName} ${d.category.label} ${d.subCategory.label}`.toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    });
  }, [all, category, q]);

  // Pull currently-hidden default IDs so we don't offer to hide them again.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/admin/hidden`)
      .then(({ data }) => {
        if (cancelled) return;
        setHiddenIds(new Set((data || []).filter((h) => h.kind === "design").map((h) => h.itemId)));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleSelect = (id) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearSelection = () => setSelected(new Set());

  const visibleIds = useMemo(
    () => filtered.slice(0, shown).map((d) => d.id).filter((id) => !hiddenIds.has(id)),
    [filtered, shown, hiddenIds]
  );
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleSelectAllVisible = () =>
    setSelected((s) => {
      const next = new Set(s);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });

  const bulkHide = async () => {
    if (selected.size === 0) return;
    if (
      !window.confirm(
        `Delete ${selected.size} built-in design${selected.size === 1 ? "" : "s"} from your site? They will no longer show up in any category. You can bring them back later from Store Info > Hidden items.`
      )
    )
      return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      await Promise.all(
        ids.map((id) =>
          axios
            .post(`${API}/admin/hidden`, { kind: "design", itemId: id })
            .catch(() => null)
        )
      );
      setHiddenIds((s) => {
        const next = new Set(s);
        ids.forEach((id) => next.add(id));
        return next;
      });
      clearSelection();
    } finally {
      setBulkBusy(false);
    }
  };

  const importAndEdit = async (d) => {
    setBusyId(d.id);
    try {
      const payload = {
        name: d.name,
        category: d.category.slug,
        subCategory: d.subCategory.slug,
        styleName: d.styleName,
        image: d.image,
        gallery: d.gallery || [],
        video: d.video || "",
        weight: Number(d.weight) || 0,
        weightUnit: d.weightUnit || "g",
        purity: d.purity || "22k",
        hallmark: d.hallmark || "BIS Hallmark 916",
        makingCharge: d.makingCharge || "On request",
        description: "",
        isExclusive: false,
      };
      const { data } = await axios.post(
        `${API}/admin/designs/import-default`,
        payload,
        { params: { original_id: d.id } }
      );
      onImport(data);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="admin-modal-scrim" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-head">
          <div>
            <h3>Browse & Edit built-in designs</h3>
            <p className="admin-muted">
              Click <strong>Edit</strong> to make an editable copy in your custom
              library, or tick the checkboxes and <strong>Delete selected</strong>
              &nbsp;to permanently remove any built-ins you don&apos;t want on the site.
            </p>
          </div>
          <button type="button" className="admin-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="admin-modal-filters">
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setShown(PAGE);
            }}
            data-testid="browse-category"
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.label}
              </option>
            ))}
          </select>
          <input
            type="search"
            placeholder="Search by style, name…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setShown(PAGE);
            }}
            data-testid="browse-search"
          />
          <span className="admin-muted" style={{ marginLeft: "auto" }}>
            {filtered.length} results
          </span>
        </div>

        <div className="admin-modal-bulkbar">
          <label className="admin-modal-selectall">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleSelectAllVisible}
              disabled={visibleIds.length === 0}
              data-testid="browse-select-all"
            />
            <span>
              {allVisibleSelected ? "Unselect all visible" : "Select all visible"}
            </span>
          </label>
          <span className="admin-modal-selcount">
            {selected.size > 0 ? `${selected.size} selected` : "None selected"}
          </span>
          {selected.size > 0 && (
            <>
              <button
                type="button"
                className="btn btn-outline"
                onClick={clearSelection}
                data-testid="browse-clear-selection"
              >
                Clear
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={bulkHide}
                disabled={bulkBusy}
                data-testid="browse-bulk-hide"
              >
                {bulkBusy ? (
                  <>
                    <Loader2 size={14} className="fu-spin" /> Deleting…
                  </>
                ) : (
                  <>
                    <Trash2 size={13} /> Delete selected ({selected.size})
                  </>
                )}
              </button>
            </>
          )}
        </div>

        <div className="admin-modal-body">
          {filtered.length === 0 ? (
            <p className="admin-muted">No designs match. Try different filters.</p>
          ) : (
            <div className="browse-grid">
              {filtered.slice(0, shown).map((d) => {
                const isHidden = hiddenIds.has(d.id);
                const isSelected = selected.has(d.id);
                return (
                  <div
                    className={`browse-card ${isSelected ? "on" : ""} ${isHidden ? "off" : ""}`}
                    key={d.id}
                  >
                    <label
                      className="browse-check"
                      onClick={(e) => e.stopPropagation()}
                      title={isHidden ? "Already hidden" : "Select"}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(d.id)}
                        disabled={isHidden}
                        data-testid={`browse-check-${d.id}`}
                      />
                    </label>
                    <div className="browse-thumb">
                      <img src={d.image} alt={d.name} loading="lazy" />
                      {isHidden && <span className="browse-hidden-tag">Deleted</span>}
                    </div>
                    <div className="browse-info">
                      <strong>{d.name}</strong>
                      <span>
                        {d.category.label} · {d.weight}
                        {d.weightUnit} · {d.purity}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary browse-edit"
                      disabled={busyId === d.id || isHidden}
                      onClick={() => importAndEdit(d)}
                      data-testid={`browse-edit-${d.id}`}
                    >
                      {busyId === d.id ? (
                        <>
                          <Loader2 size={14} className="fu-spin" /> Importing…
                        </>
                      ) : (
                        <>
                          <Pencil size={13} /> Edit
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {shown < filtered.length && (
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setShown((s) => s + PAGE)}
              >
                Show more ({filtered.length - shown} remaining)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DesignsTab;

// ---------------------------------------------------------------------------
// Bulk-fill AI descriptions
// ---------------------------------------------------------------------------
function BulkDescriptionsButton({ onDone }) {
  const [pending, setPending] = React.useState(null);
  const [running, setRunning] = React.useState(false);
  const [progress, setProgress] = React.useState({ index: 0, total: 0, name: "" });
  const [errors, setErrors] = React.useState([]);
  const [complete, setComplete] = React.useState(false);

  // Refresh the "N missing" count on mount and whenever the caller reloads.
  React.useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/admin/ai/bulk-descriptions/pending`)
      .then((r) => alive && setPending(r.data))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const run = async () => {
    if (running) return;
    setRunning(true);
    setErrors([]);
    setComplete(false);
    setProgress({ index: 0, total: pending?.pending || 0, name: "" });
    try {
      const token = localStorage.getItem("alankar_token");
      const resp = await fetch(`${API}/admin/ai/bulk-descriptions`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const chunk = await reader.read();
        if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const frame of frames) {
          const line = frame.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.kind === "start") {
              setProgress({ index: 0, total: ev.total, name: "" });
            } else if (ev.kind === "item") {
              setProgress({ index: ev.index, total: ev.total, name: ev.name || "" });
              if (ev.status === "error") {
                setErrors((e) => [...e, { name: ev.name, error: ev.error }]);
              }
            } else if (ev.kind === "done") {
              setComplete(true);
            }
          } catch (_) {
            /* ignore malformed frames */
          }
        }
      }
      // Refresh count + parent list
      const r = await axios.get(`${API}/admin/ai/bulk-descriptions/pending`);
      setPending(r.data);
      if (onDone) onDone();
    } catch (e) {
      setErrors((prev) => [...prev, { name: "Bulk job", error: e.message || String(e) }]);
    } finally {
      setRunning(false);
    }
  };

  const missing = pending?.pending ?? 0;
  const label = missing > 0
    ? `✨ Fill ${missing} missing description${missing === 1 ? "" : "s"} with AI`
    : "✨ All descriptions filled";

  return (
    <>
      <button
        type="button"
        className="btn btn-outline"
        onClick={run}
        disabled={running || missing === 0}
        data-testid="admin-bulk-desc-btn"
        style={{ opacity: missing === 0 ? 0.55 : 1 }}
      >
        {running ? (
          <>
            <Loader2 size={14} className="spin" /> Writing {progress.index}/{progress.total}…
          </>
        ) : (
          label
        )}
      </button>

      {(running || complete || errors.length > 0) && (
        <div className="admin-bulk-toast" data-testid="admin-bulk-toast">
          {running && (
            <>
              <div className="admin-bulk-bar">
                <div
                  className="admin-bulk-bar-fill"
                  style={{ width: progress.total ? `${(progress.index / progress.total) * 100}%` : "5%" }}
                />
              </div>
              <div className="admin-bulk-line">
                Writing {progress.index}/{progress.total} · {progress.name || "…"}
              </div>
            </>
          )}
          {complete && !running && (
            <div className="admin-bulk-line admin-bulk-done">
              Done — filled {progress.total - errors.length}/{progress.total}
              {errors.length > 0 ? ` (${errors.length} failed)` : ""}.
            </div>
          )}
          {errors.length > 0 && (
            <ul className="admin-bulk-errs">
              {errors.slice(0, 5).map((er, i) => (
                <li key={i}>
                  <strong>{er.name}:</strong> {er.error}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// Re-compress uploaded videos (batch)
// ---------------------------------------------------------------------------
function OptimizeVideosButton() {
  const [status, setStatus] = React.useState(null);
  const [running, setRunning] = React.useState(false);
  const [progress, setProgress] = React.useState({ index: 0, total: 0 });
  const [results, setResults] = React.useState({ ok: 0, skip: 0, err: 0, saved: 0 });
  const [errors, setErrors] = React.useState([]);
  const [complete, setComplete] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/admin/media/optimize-videos/status`)
      .then((r) => alive && setStatus(r.data))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const humanBytes = (n) => {
    if (!n) return "0 B";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const run = async () => {
    if (running) return;
    if (!window.confirm("Re-compress every uploaded video to a mobile-friendly size? This may take a few minutes and does not touch external CDN videos.")) return;
    setRunning(true);
    setErrors([]);
    setComplete(false);
    setResults({ ok: 0, skip: 0, err: 0, saved: 0 });
    setProgress({ index: 0, total: status?.in_storage || 0 });
    try {
      const token = localStorage.getItem("alankar_token");
      const resp = await fetch(`${API}/admin/media/optimize-videos`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const chunk = await reader.read();
        if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const frame of frames) {
          const line = frame.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.kind === "start") {
              setProgress({ index: 0, total: ev.total });
            } else if (ev.kind === "progress") {
              setProgress({ index: ev.index, total: ev.total });
            } else if (ev.kind === "ok") {
              setResults((r) => ({ ...r, ok: r.ok + 1, saved: r.saved + (ev.saved || 0) }));
            } else if (ev.kind === "skip") {
              setResults((r) => ({ ...r, skip: r.skip + 1 }));
            } else if (ev.kind === "error") {
              setResults((r) => ({ ...r, err: r.err + 1 }));
              setErrors((e) => [...e, { id: ev.id, msg: ev.msg }]);
            } else if (ev.kind === "done") {
              setComplete(true);
            }
          } catch (_) {
            /* ignore malformed frames */
          }
        }
      }
      const r = await axios.get(`${API}/admin/media/optimize-videos/status`);
      setStatus(r.data);
    } catch (e) {
      setErrors((prev) => [...prev, { id: "job", msg: e.message || String(e) }]);
    } finally {
      setRunning(false);
    }
  };

  const inStorage = status?.in_storage ?? 0;
  const label = inStorage > 0
    ? `⚡ Re-compress ${inStorage} uploaded video${inStorage === 1 ? "" : "s"}`
    : "⚡ No uploaded videos to compress";

  return (
    <>
      <button
        type="button"
        className="btn btn-outline"
        onClick={run}
        disabled={running || inStorage === 0}
        data-testid="admin-optimize-videos-btn"
        style={{ opacity: inStorage === 0 ? 0.55 : 1 }}
      >
        {running ? (
          <>
            <Loader2 size={14} className="spin" /> Compressing {progress.index}/{progress.total}…
          </>
        ) : (
          label
        )}
      </button>

      {(running || complete || errors.length > 0) && (
        <div className="admin-bulk-toast" data-testid="admin-optimize-toast">
          {running && (
            <>
              <div className="admin-bulk-bar">
                <div
                  className="admin-bulk-bar-fill"
                  style={{ width: progress.total ? `${(progress.index / progress.total) * 100}%` : "5%" }}
                />
              </div>
              <div className="admin-bulk-line">
                Compressing {progress.index}/{progress.total} · {results.ok} shrunk · {results.skip} already-small
              </div>
            </>
          )}
          {complete && !running && (
            <div className="admin-bulk-line admin-bulk-done">
              Done — {results.ok} shrunk, saved {humanBytes(results.saved)} · {results.skip} skipped · {results.err} failed.
            </div>
          )}
          {errors.length > 0 && (
            <ul className="admin-bulk-errs">
              {errors.slice(0, 5).map((er, i) => (
                <li key={i}>
                  <strong>{er.id}:</strong> {er.msg}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Re-compress uploaded images (batch) — mirrors OptimizeVideosButton
// ---------------------------------------------------------------------------
function OptimizeImagesButton() {
  const [status, setStatus] = React.useState(null);
  const [running, setRunning] = React.useState(false);
  const [progress, setProgress] = React.useState({ index: 0, total: 0 });
  const [results, setResults] = React.useState({ ok: 0, skip: 0, err: 0, saved: 0 });
  const [errors, setErrors] = React.useState([]);
  const [complete, setComplete] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/admin/media/optimize-images/status`)
      .then((r) => alive && setStatus(r.data))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const humanBytes = (n) => {
    if (!n) return "0 B";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const run = async () => {
    if (running) return;
    if (!window.confirm("Re-compress every uploaded image bigger than 500 KB to a mobile-friendly JPEG? External CDN images are skipped.")) return;
    setRunning(true);
    setErrors([]);
    setComplete(false);
    setResults({ ok: 0, skip: 0, err: 0, saved: 0 });
    setProgress({ index: 0, total: status?.in_storage || 0 });
    try {
      const token = localStorage.getItem("alankar_token");
      const resp = await fetch(`${API}/admin/media/optimize-images`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const chunk = await reader.read();
        if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const frame of frames) {
          const line = frame.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.kind === "start") {
              setProgress({ index: 0, total: ev.total });
            } else if (ev.kind === "progress") {
              setProgress({ index: ev.index, total: ev.total });
            } else if (ev.kind === "ok") {
              setResults((r) => ({ ...r, ok: r.ok + 1, saved: r.saved + (ev.saved || 0) }));
            } else if (ev.kind === "skip") {
              setResults((r) => ({ ...r, skip: r.skip + 1 }));
            } else if (ev.kind === "error") {
              setResults((r) => ({ ...r, err: r.err + 1 }));
              setErrors((e) => [...e, { id: ev.id, msg: ev.msg }]);
            } else if (ev.kind === "done") {
              setComplete(true);
            }
          } catch (_) {
            /* ignore malformed frames */
          }
        }
      }
      const r = await axios.get(`${API}/admin/media/optimize-images/status`);
      setStatus(r.data);
    } catch (e) {
      setErrors((prev) => [...prev, { id: "job", msg: e.message || String(e) }]);
    } finally {
      setRunning(false);
    }
  };

  const inStorage = status?.in_storage ?? 0;
  const label = inStorage > 0
    ? `🖼️ Re-compress ${inStorage} uploaded image${inStorage === 1 ? "" : "s"}`
    : "🖼️ No uploaded images to compress";

  return (
    <>
      <button
        type="button"
        className="btn btn-outline"
        onClick={run}
        disabled={running || inStorage === 0}
        data-testid="admin-optimize-images-btn"
        style={{ opacity: inStorage === 0 ? 0.55 : 1 }}
      >
        {running ? (
          <>
            <Loader2 size={14} className="spin" /> Shrinking {progress.index}/{progress.total}…
          </>
        ) : (
          label
        )}
      </button>

      {(running || complete || errors.length > 0) && (
        <div className="admin-bulk-toast" data-testid="admin-optimize-images-toast">
          {running && (
            <>
              <div className="admin-bulk-bar">
                <div
                  className="admin-bulk-bar-fill"
                  style={{ width: progress.total ? `${(progress.index / progress.total) * 100}%` : "5%" }}
                />
              </div>
              <div className="admin-bulk-line">
                Shrinking {progress.index}/{progress.total} · {results.ok} shrunk · {results.skip} skipped
              </div>
            </>
          )}
          {complete && !running && (
            <div className="admin-bulk-line admin-bulk-done">
              Done — {results.ok} shrunk, saved {humanBytes(results.saved)} · {results.skip} skipped · {results.err} failed.
            </div>
          )}
          {errors.length > 0 && (
            <ul className="admin-bulk-errs">
              {errors.slice(0, 5).map((er, i) => (
                <li key={i}>
                  <strong>{er.id}:</strong> {er.msg}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// AI Studio — multi-angle photos → Nano Banana cleanup + Sora cinematic clip
// ---------------------------------------------------------------------------
function AiStudioSection({ onApply }) {
  const [files, setFiles] = React.useState([]);
  const [pairs, setPairs] = React.useState([]); // {idx, originalUrl, cleanedUrl, choice, sharpness, error}
  const [running, setRunning] = React.useState(false);
  const [progress, setProgress] = React.useState({ index: 0, total: 0 });
  const [bestIdx, setBestIdx] = React.useState(null);
  const [videoUrl, setVideoUrl] = React.useState("");
  const [genVideo, setGenVideo] = React.useState(false);
  const [videoErr, setVideoErr] = React.useState("");
  const [mainIdx, setMainIdx] = React.useState(null);

  const backendBase = process.env.REACT_APP_BACKEND_URL;
  const absoluteUrl = (u) => (u && u.startsWith("http") ? u : `${backendBase}${u || ""}`);

  const handleFiles = (fs) => {
    const list = Array.from(fs || []).filter((f) => f.type.startsWith("image/")).slice(0, 8);
    setFiles(list);
    setPairs([]);
    setVideoUrl("");
    setBestIdx(null);
    setMainIdx(null);
    setVideoErr("");
  };

  const runClean = async () => {
    if (running || files.length === 0) return;
    setRunning(true);
    setPairs([]);
    setBestIdx(null);
    setVideoUrl("");
    setVideoErr("");
    setProgress({ index: 0, total: files.length });
    try {
      const token = localStorage.getItem("alankar_token");
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const resp = await fetch(`${API}/admin/ai/studio/clean`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const chunk = await reader.read();
        if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const frame of frames) {
          const line = frame.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.kind === "start") {
              setProgress({ index: 0, total: ev.total });
            } else if (ev.kind === "pair") {
              setProgress((p) => ({ ...p, index: p.index + 1 }));
              setPairs((arr) => {
                const next = [...arr];
                next.push({
                  idx: ev.idx,
                  originalUrl: ev.originalUrl,
                  cleanedUrl: ev.cleanedUrl,
                  choice: ev.cleanedUrl ? "cleaned" : "original",
                  sharpness: ev.sharpness,
                  error: ev.error,
                });
                return next;
              });
            } else if (ev.kind === "done") {
              setBestIdx(ev.bestIdx);
              setMainIdx(ev.bestIdx);
            }
          } catch (_) {
            /* ignore */
          }
        }
      }
    } catch (e) {
      setVideoErr(e.message || "Cleanup failed — please try again.");
    } finally {
      setRunning(false);
    }
  };

  const chosenUrl = (p) => (p.choice === "cleaned" ? p.cleanedUrl || p.originalUrl : p.originalUrl);

  const runVideo = async () => {
    if (genVideo) return;
    if (mainIdx == null) return;
    const main = pairs.find((p) => p.idx === mainIdx);
    if (!main) return;
    setGenVideo(true);
    setVideoErr("");
    try {
      const token = localStorage.getItem("alankar_token");
      const r = await fetch(`${API}/admin/ai/studio/video`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ imageUrl: chosenUrl(main) }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setVideoUrl(data.videoUrl);
    } catch (e) {
      setVideoErr(e.message || "Video generation failed.");
    } finally {
      setGenVideo(false);
    }
  };

  const applyToForm = () => {
    const ordered = [...pairs].sort((a, b) => (a.idx === mainIdx ? -1 : b.idx === mainIdx ? 1 : a.idx - b.idx));
    const urls = ordered.map(chosenUrl).filter(Boolean);
    if (!urls.length) return;
    onApply({
      image: urls[0],
      gallery: urls.slice(1, 4),
      video: videoUrl || "",
    });
  };

  return (
    <div className="ai-studio" data-testid="ai-studio">
      <div className="ai-studio-head">
        <div>
          <strong>✨ AI Studio <span className="ai-studio-badge">NEW</span></strong>
          <p className="admin-muted">
            Drop 3-4 photos of the piece from different angles. Nano Banana strips
            the background &amp; sits it on black; Sora 2 spins up a 10-sec
            cinematic clip. Everything auto-fills into the fields below.
          </p>
        </div>
      </div>

      <label className="ai-studio-drop">
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          disabled={running || genVideo}
          data-testid="ai-studio-files"
        />
        <span>
          {files.length ? `${files.length} photo${files.length > 1 ? "s" : ""} selected` : "Click to pick photos (max 8)"}
        </span>
      </label>

      <div className="ai-studio-actions">
        <button
          type="button"
          className="btn btn-outline"
          disabled={running || files.length === 0}
          onClick={runClean}
          data-testid="ai-studio-clean"
        >
          {running ? (
            <>
              <Loader2 size={14} className="spin" /> Cleaning {progress.index}/{progress.total}…
            </>
          ) : (
            <>✨ Clean backgrounds ({files.length || "0"})</>
          )}
        </button>
      </div>

      {pairs.length > 0 && (
        <>
          <div className="ai-studio-grid">
            {pairs
              .slice()
              .sort((a, b) => a.idx - b.idx)
              .map((p) => (
                <div
                  key={p.idx}
                  className={`ai-studio-card ${mainIdx === p.idx ? "main" : ""}`}
                  data-testid={`ai-studio-card-${p.idx}`}
                >
                  <div className="ai-studio-media">
                    <img
                      src={absoluteUrl(chosenUrl(p))}
                      alt={`angle ${p.idx + 1}`}
                    />
                    {mainIdx === p.idx && <span className="ai-studio-mainbadge">Main</span>}
                    {bestIdx === p.idx && mainIdx !== p.idx && (
                      <span className="ai-studio-suggestbadge">Sharpest</span>
                    )}
                  </div>
                  <div className="ai-studio-toggle" role="tablist">
                    <button
                      type="button"
                      className={p.choice === "cleaned" ? "on" : ""}
                      disabled={!p.cleanedUrl}
                      onClick={() =>
                        setPairs((arr) => arr.map((x) => (x.idx === p.idx ? { ...x, choice: "cleaned" } : x)))
                      }
                      data-testid={`ai-studio-cleaned-${p.idx}`}
                    >
                      AI clean
                    </button>
                    <button
                      type="button"
                      className={p.choice === "original" ? "on" : ""}
                      onClick={() =>
                        setPairs((arr) => arr.map((x) => (x.idx === p.idx ? { ...x, choice: "original" } : x)))
                      }
                      data-testid={`ai-studio-original-${p.idx}`}
                    >
                      Original
                    </button>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline ai-studio-setmain"
                    onClick={() => setMainIdx(p.idx)}
                    disabled={mainIdx === p.idx}
                  >
                    {mainIdx === p.idx ? "Main photo" : "Use as main"}
                  </button>
                  {p.error && <div className="admin-bulk-line" style={{ color: "#B04040" }}>{p.error}</div>}
                </div>
              ))}
          </div>

          <div className="ai-studio-actions">
            <button
              type="button"
              className="btn btn-outline"
              disabled={genVideo || mainIdx == null}
              onClick={runVideo}
              data-testid="ai-studio-video"
            >
              {genVideo ? (
                <>
                  <Loader2 size={14} className="spin" /> Filming 10-sec clip (~40-90 s)…
                </>
              ) : videoUrl ? (
                "🎬 Re-generate video"
              ) : (
                "🎬 Generate 10-sec cinematic clip"
              )}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={running || genVideo || mainIdx == null}
              onClick={applyToForm}
              data-testid="ai-studio-apply"
            >
              Apply to design form
            </button>
          </div>
          {videoErr && <div className="admin-bulk-line" style={{ color: "#B04040" }}>{videoErr}</div>}

          {videoUrl && (
            <video
              className="ai-studio-preview"
              src={absoluteUrl(videoUrl)}
              controls
              loop
              muted
              playsInline
              data-testid="ai-studio-preview"
            />
          )}
        </>
      )}
    </div>
  );
}

